var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { tokens } from './tokens/js/tokens-esm';
/**
 * This function is adding agnostic
 * palette classes to the tailwind config.
 *
 * Example class:
 * `.n-palette-light-primary-text-default`
 * will point to the scoped CSS variable:
 * `--theme-palette-primary-text-default`
 */
const themedPalette = () => {
    const result = {};
    const extractValue = (input, flatkey = '') => {
        if (typeof input === 'object') {
            Object.keys(input).forEach((key) => {
                extractValue(input[key], `${flatkey}-${key}`);
            });
        }
        if (typeof input === 'string') {
            /**
             * Values will always be in the RGBA form:
             * - 255, 255, 255 / 0.5
             * And we wrap them to rgb() CSS function
             */
            flatkey = flatkey.replace('light-', '');
            result[flatkey] = `rgb(var(--theme-${flatkey}))`;
        }
    };
    extractValue(tokens.theme.light.palette, 'palette');
    return result;
};
const _a = tokens.zIndex, { alias: zIndexAlias } = _a, zIndexWithoutAlias = __rest(_a, ["alias"]);
/**
 * This function is adding agnostic
 * shadow classes to the tailwind config.
 *
 * Example class:
 * `.n-shadow-overlay`
 * will point to the scoped CSS variable:
 * `--theme-shadow-overlay`
 */
const themedShadows = () => {
    const result = {};
    const extractValue = (input, flatkey = '') => {
        if (typeof input === 'object') {
            Object.keys(input).forEach((key) => {
                extractValue(input[key], `${flatkey}-${key}`);
            });
        }
        if (typeof input === 'string') {
            flatkey = flatkey.replace('light-', '');
            // Remove "shadow-" prefix as tailwind appends it automatically
            const key = flatkey.replace('shadow-', '');
            result[key] = `var(--theme-${flatkey})`;
        }
    };
    extractValue(tokens.theme.light.boxShadow, 'shadow');
    return result;
};
/** Prefixes all keys of an object with a given string, used for flat objects only */
const prefixProperties = (obj, prefix) => Object.keys(obj).reduce((acc, curr) => {
    acc[`${prefix}-${curr}`] = obj[curr];
    return acc;
}, {});
/** @type {import('tailwindcss').Config['theme']} */
export const tailwindTheme = {
    colors: Object.assign(Object.assign(Object.assign({}, tokens.colors), { dark: Object.assign({}, tokens.theme.dark.palette), light: Object.assign({}, tokens.theme.light.palette) }), themedPalette()),
    borderRadius: tokens.borderRadius,
    boxShadow: Object.assign(Object.assign(Object.assign({}, prefixProperties(tokens.theme.dark.boxShadow, 'dark')), prefixProperties(tokens.theme.light.boxShadow, 'light')), themedShadows()),
    /**
     * Avoid colors being generated as shadow color classes
     * Source: https://github.com/tailwindlabs/tailwindcss/discussions/11933
     */
    boxShadowColor: {},
    zIndex: Object.assign(Object.assign({}, zIndexAlias), zIndexWithoutAlias),
    fontFamily: {
        sans: ['"Public Sans"'],
        mono: ['"Fira Code"'],
        syne: ['"Syne Neo"'],
    },
    screens: Object.assign({}, tokens.breakpoints),
    /** Use our custom animation tokens */
    transitionTimingFunction: {
        DEFAULT: tokens.transitions.values['timing-function'].default,
    },
    transitionDuration: {
        DEFAULT: tokens.transitions.values.duration.quick,
        quick: tokens.transitions.values.duration.quick,
        slow: tokens.transitions.values.duration.slow,
    },
    transitionProperty: {
        all: tokens.transitions.values.properties.default,
    },
};
//# sourceMappingURL=tailwindTheme.js.map